import type { PushSdkInternalImpl } from '../push-sdk-internal-impl'
import type { PushSDK } from '../push-sdk'
import { Result } from '@core/models/result'
import { PNException } from '@core/exceptions/pn-exception.ts'
import { PNExceptionCode } from '@core/exceptions/pn-exception-code.ts'

/**
 * Type predicate ensuring passed object is a {@link PushSDK} instance.
 *
 * @example
 * ```typescript
 * const possibleSDK: unknown
 *
 * possibleSDK.setConfiguration({ domainKey: 'abc123' }) // TypeError as `possibleSDK` may or may not satisfy `PushSDK`
 *
 * if (isPushSDK(possibleSDK)) {
 *   possibleSDK.setConfiguration({ domainKey: 'abc123' }) // Safe invocation with a guaranteed `PushSDK`
 * } else {
 *   // Not a `PushSDK`
 * }
 * ```
 */
function isPushSDK(sdk: unknown): sdk is PushSDK {
    return !!sdk && typeof sdk === 'object' && 'iid' in sdk && typeof sdk.iid === 'string'
}

/**
 * Returns the current global {@link window.PushlySDK} instance
 * if it exists and contains an [iid]{@link PushSdkInternalImpl#iid}.
 *
 * @internal
 */
export function getGlobalPushSdk(): PushSDK | null {
    const globalInstance: PushSDK = window['PushlySDK']
    if (isPushSDK(globalInstance)) {
        return globalInstance
    }
    return null
}

/**
 * Ensures only a single instance of the {@link window.PushlySDK}
 * is running at a time.
 *
 * If the current instance matches the passed instance and the
 * sdk is already marked as loaded we should exit to prevent
 * attempts to invoke load more than once.
 *
 * Returns:
 * - Result.Ok(true)
 * - Result.Err({@link PNException})
 *   - {@link PNExceptionCode.DUPLICATE_SDK_DETECTED}
 *   - {@link PNExceptionCode.SDK_ALREADY_LOADED}
 *
 * @internal
 */
export function validateSingleInstance(sdk: PushSdkInternalImpl): Result<boolean, PNException> {
    const globalInstance = getGlobalPushSdk()

    if (globalInstance?.iid !== undefined && globalInstance.iid !== sdk.iid) {
        return Result.Err(new PNException(PNExceptionCode.DUPLICATE_SDK_DETECTED))
    }

    if (sdk.isLoaded) {
        return Result.Err(new PNException(PNExceptionCode.SDK_ALREADY_LOADED))
    }

    return Result.Ok(true)
}
